// Batches conversation-badge lookups so the conversation list makes one request per burst of cards
// instead of one per row. Each card registers its conversation id; a short debounce flushes the
// unknown ids together and caches the result (conversation_id → { pipeline_name, stage_name,
// stage_color } | null). Null means "checked, no deal" so we never re-request it.
import { computed, reactive, toValue, watch } from 'vue';
import { conversationBadges } from './api';

const cache = reactive({});
const pending = new Set();
let timer = null;

const flush = async () => {
  timer = null;
  const ids = [...pending];
  pending.clear();
  const unknown = ids.filter(id => cache[id] === undefined);
  if (!unknown.length) return;
  unknown.forEach(id => {
    cache[id] = null; // mark as requested to avoid duplicate fetches
  });
  try {
    const { data } = await conversationBadges(unknown);
    Object.entries(data.payload || {}).forEach(([id, badge]) => {
      cache[Number(id)] = badge;
    });
  } catch (e) {
    // leave as null; a later mount can retry after cache reset
  }
};

const register = id => {
  if (!id || cache[id] !== undefined) return;
  pending.add(id);
  if (!timer) timer = setTimeout(flush, 150);
};

// Accepts a plain id, a ref or a getter. The conversation list recycles its rows through a
// virtualizer, so the same component instance can move to another conversation after setup —
// the id must be watched, not read once.
export function useConversationBadge(conversationId) {
  const id = computed(() => Number(toValue(conversationId)) || 0);
  watch(id, register, { immediate: true });
  return computed(() => (id.value ? cache[id.value] : null));
}
